import React, { useEffect } from "react";
import DashboardLineColorName from "./DashboardLineColorName";

/**
 * The per-line totals popup the Line Color sort opens on Family Members and on
 * Family Members (Since 2000): one row per family line, the head of the line
 * and its colour in that colour, then how many of the rows on screen it holds —
 *
 *     Mary M • Dk Blue Line - 1
 *     Ida • Dk Green Line - 10
 *
 * Counted off the rows the page is showing, so under a member tab, a year pick
 * or a search the lines still add up to the count in the heading.
 *
 * Males/Females passes `sections` instead of `totals`: one titled list per
 * gender, each counted off that gender's column —
 *
 *     Males (129)                Females (97)
 *     Mary M • Dk Blue Line - 1  Mary M • Dk Blue Line - 3
 */

/**
 * Colour + hex, normalised the way the legend builder normalises them, so a row
 * and the legend entry for the same family line land on the same key.
 */
export const familyLineKey = (colorName, hexCode) =>
  `${String(colorName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+line$/i, "")}|${String(hexCode || "").toLowerCase()}`;

/**
 * Totals per family line, in the order the family-line key reads in (oldest
 * head first). A line the key does not know follows the rest, alphabetically.
 *
 * @param {Array<{colorName: string, hexCode: string}>} entries one per row on screen
 * @param {Array<{headName: string, colorName: string, hexCode: string}>} legendLines
 */
export function buildDashboardLineTotals(entries, legendLines) {
  const totals = new Map();
  entries.forEach(({ colorName, hexCode }) => {
    if (!colorName) return;
    const key = familyLineKey(colorName, hexCode);
    const entry = totals.get(key) || { key, colorName, hexCode, count: 0 };
    entry.count += 1;
    totals.set(key, entry);
  });

  // Keyed on colour + hex, with a colour-only fallback for rows whose hex is
  // spelled differently from the heads-of-line record's.
  const byKey = new Map();
  legendLines.forEach((line, index) => {
    const value = { index, headName: line.headName || "" };
    const key = familyLineKey(line.colorName, line.hexCode);
    if (!byKey.has(key)) byKey.set(key, value);
    const colorOnly = familyLineKey(line.colorName, "");
    if (!byKey.has(colorOnly)) byKey.set(colorOnly, value);
  });
  const lookup = (entry) =>
    byKey.get(entry.key) || byKey.get(familyLineKey(entry.colorName, ""));

  return [...totals.values()]
    .map((entry) => ({
      ...entry,
      headName: lookup(entry)?.headName || "",
      rank: lookup(entry)?.index ?? Infinity,
    }))
    .sort(
      (a, b) => a.rank - b.rank || a.colorName.localeCompare(b.colorName)
    );
}

function LineTotalsList({ totals }) {
  return (
    <div className="dashboard-line-totals-list">
      {totals.map((line) => (
        <div key={line.key} className="dashboard-line-totals-row">
          <DashboardLineColorName
            colorName={line.colorName}
            hexCode={line.hexCode}
            headName={line.headName}
          />
          <span style={{ color: line.hexCode }}>{` - ${line.count}`}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * @param {Array} [totals] one list of per-line totals
 * @param {Array<{key: string, title: string, count: number, className?: string, totals: Array}>} [sections]
 *   titled lists side by side (stacked on a phone), in place of `totals`
 */
export default function DashboardLineTotalsPopup({
  open,
  onClose,
  totals = [],
  sections,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const hasSections = Array.isArray(sections) && sections.length > 0;
  if (!open) return null;
  if (hasSections ? sections.every((s) => s.totals.length === 0) : totals.length === 0)
    return null;

  return (
    <div
      className="dashboard-line-totals-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="dashboard-line-totals-popup"
        role="dialog"
        aria-modal="true"
        aria-label="Totals by family line"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="dashboard-line-totals-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        {hasSections ? (
          <div className="dashboard-line-totals-sections">
            {sections.map((section) => (
              <div key={section.key} className="dashboard-line-totals-section">
                <h4
                  className={`dashboard-line-totals-section-title ${section.className || ""}`}
                >
                  {section.title} ({section.count})
                </h4>
                {section.totals.length === 0 ? (
                  <p className="dashboard-line-totals-empty">None</p>
                ) : (
                  <LineTotalsList totals={section.totals} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <LineTotalsList totals={totals} />
        )}
      </div>
    </div>
  );
}
